import * as React from 'react'
import styled from 'styled-components'

import { OTPMethod } from '@ally/transmitigator'
import { Box, Button } from '@ally/metronome-ui'
import { PHONE_NUMBER_PATTERN } from '@ally/aos-utils'
import { Form, PhoneNumber, useFormsContext } from '@ally/forms'

const InputRow = styled(Box)`
  ${({ theme: { media, space } }): string => `
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: ${space.xs};
    ${media.down('md')} {
      flex-direction: column;
      margin-bottom: 0;
    }
  `}
`

interface PhoneNumberFormProps {
  formId: string
  hasDeliveryOptions: boolean
  otpMethods: OTPMethod[]
}

export const PhoneNumberForm: React.FC<PhoneNumberFormProps> = ({
  formId,
  hasDeliveryOptions,
  otpMethods,
}) => {
  const { submitForm, resetForm, isValid, values } = useFormsContext()

  const digitsOnly = (phoneStr: string): string =>
    // eslint-disable-next-line no-useless-escape
    phoneStr.replace(/[- \(\)]/gi, '')

  const validate = (value: string): string | void => {
    if (!value && hasDeliveryOptions) {
      return undefined
    }

    if (!value) {
      return 'Enter a phone number to continue.'
    }

    if (!PHONE_NUMBER_PATTERN.test(value)) {
      return 'Enter a valid phone number.'
    }

    const { phoneNumber } = values
    const cleanPhoneNumber = digitsOnly(phoneNumber)

    const phoneNumberAlreadyExists = otpMethods.some(
      methods => digitsOnly(methods.value) === cleanPhoneNumber,
    )
    if (phoneNumberAlreadyExists) {
      return 'This number is already saved in your delivery preferences.'
    }

    return undefined
  }

  const handleSubmit = (): void => {
    if (!!values.phoneNumber && isValid) {
      submitForm().then(() => {
        resetForm()
      })
    }
  }

  return (
    <Form id={formId}>
      <InputRow>
        <Box width="12">
          <PhoneNumber
            data-testid="setup-security-phone"
            name="phoneNumber"
            label="Phone number"
            data-allytmfn="phoneNumber"
            required
            isPrivate
            validate={validate}
          />
        </Box>
        <Button
          type="button"
          variant="outline"
          onClick={handleSubmit}
          text="Add"
          mt="lg"
          ml="xxs"
        />
      </InputRow>
    </Form>
  )
}
